import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ManualData from "@/components/ManualData";
import SupportVaultDrawer from "@/components/SupportVaultDrawer";
import { CockpitTelemetryScroller, SheetLightningOverlay, MicroStarsCanvas, SpaceAgeCursorMount } from "@/components/dashboard/SpaceAgeControlDeck";
import {
  LayoutDashboard,
  Receipt,
  Upload,
  Inbox,
  Banknote,
  Building2,
  CreditCard,
  LogOut,
  Sparkles,
  BookOpen,
  LifeBuoy,
  Satellite,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview", tid: "nav-dashboard" },
  { to: "/receipts", icon: Receipt, label: "Receipts", tid: "nav-receipts" },
  { to: "/upload", icon: Upload, label: "Scan", tid: "nav-upload" },
  { to: "/inbox", icon: Inbox, label: "Ingestion", tid: "nav-inbox" },
  { to: "/bank-feed", icon: Banknote, label: "Bank Feed", tid: "nav-bankfeed" },
  { to: "/enterprise", icon: Building2, label: "Enterprise", tid: "nav-enterprise" },
  { to: "/profile", icon: Satellite, label: "Control Deck", tid: "nav-control-deck", emoji: "🛰️" },
  { to: "/billing", icon: CreditCard, label: "Billing", tid: "nav-billing" },
];

export default function DashboardLayout({ children }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showManual, setShowManual] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const tier = (user?.subscription_tier || "").toLowerCase();
  const isPremium = tier === "premium_shield" || tier === "enterprise_fleet" || Boolean(user?.is_admin);
  const canSeeManual = tier === "pro" || tier === "business" || isPremium;

  const handleLevelUp = async () => {
    if (upgrading) return;
    setUpgrading(true);
    const toastId = toast.loading("Prorating your upgrade to Premium Shield…");
    try {
      const { data } = await api.post("/payments/upgrade", {
        lookup_key: "premium_shield_monthly",
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        // Fallback: no active sub — redirect to Stripe checkout
        window.location.href = data.checkout_url;
        return;
      }
      if (data?.already_on_tier) {
        toast.success("You're already on Premium Shield.", { id: toastId });
      } else if (data?.prorated) {
        toast.success("Upgraded to Premium Shield · prorated instantly", {
          id: toastId,
          description: "New features unlock on your next dashboard refresh.",
        });
        if (setUser && user) {
          setUser({ ...user, subscription_tier: "premium_shield", subscription_status: "active" });
        }
      } else {
        toast.success("Upgrade complete", { id: toastId });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete upgrade — redirecting to Paywall", { id: toastId });
      window.location.assign("/paywall?upgrade=premium_shield_monthly");
    } finally {
      setUpgrading(false);
    }
  };

  // Ambient mouse-follow glow for nav items — updates CSS vars on hover.
  const onNavMove = (e) => {
    const t = e.currentTarget;
    const r = t.getBoundingClientRect();
    t.style.setProperty("--gx", `${e.clientX - r.left}px`);
    t.style.setProperty("--gy", `${e.clientY - r.top}px`);
  };

  return (
    <div
      className="min-h-screen text-slate-100 overflow-x-clip relative"
      style={{ isolation: "isolate" }}
    >
      {/* Cosmic nebula image canvas — hard-stretched 100% x 100% to viewport,
          Gemini watermark stripped at ingest. Zero shield/veil above it so
          the turquoise + orange + pink cloud fields render at raw intensity. */}
      <div
        aria-hidden="true"
        data-testid="cosmic-nebula-backdrop"
        className="fixed inset-0 z-0 object-cover pointer-events-none bg-[length:100%_100%] bg-no-repeat"
        style={{ backgroundImage: "url('/assets/cosmic-nebula-core.png')" }}
      />
      {/* Distant micro-stars field — 40-60 micro-stars (radius 0.5-1px) at
          z-index: -10, between the nebula (z:-20) and the lightning (z:-1).
          Each star oscillates at its own frequency for an organic stellar
          expanse effect. Silent. pointer-events:none. */}
      <MicroStarsCanvas />
      {/* Timed sheet-lightning canvas (every 12-24s) — branching electric-cyan
          bolt trajectories drawn on an HTML5 canvas at z-index:-1. The outer
          `isolation:isolate` stacking context puts this canvas directly in
          front of the nebula backdrop (z:-20) but safely behind all glass
          cards (z:auto). pointer-events:none enforced in CSS. */}
      <SheetLightningOverlay />
      {/* Slim scrolling cockpit telemetry banner — very top edge, above the app nav */}
      <CockpitTelemetryScroller />
      {/* Top nav */}
      <header
        className="sticky top-0 z-40 glass-strong border-b border-white/10"
        data-testid="app-header"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4 sm:gap-6">
          <div
            className="flex items-center gap-6 cursor-pointer"
            onClick={() => navigate("/dashboard")}
          >
            <Logo size="md" />
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.tid}
                onMouseMove={onNavMove}
                className={({ isActive }) =>
                  `nav-glow relative overflow-hidden px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    isActive
                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      : "text-slate-300 hover:text-white hover:bg-white/5 border border-transparent"
                  }`
                }
              >
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {canSeeManual && (
              <button
                onClick={() => setShowManual(true)}
                data-testid="open-manual-btn"
                className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 text-emerald-200 hover:text-white hover:bg-emerald-500/15 transition-colors"
                title="Docs & Manual"
              >
                <BookOpen size={12} />
                Docs & Manual
              </button>
            )}
            <button
              onClick={() => setShowSupport(true)}
              data-testid="open-support-btn"
              className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md border border-cyan-500/30 bg-cyan-500/5 text-cyan-200 hover:text-white hover:bg-cyan-500/15 transition-colors"
              title="Help Desk & Support Vault"
            >
              <LifeBuoy size={12} />
              Help Desk
            </button>
            <span
              className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-md ${
                isPremium
                  ? "premium-shield-tier-badge"
                  : "border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              }`}
              data-testid="tier-badge"
              data-tier={tier || "pro"}
            >
              <Sparkles size={12} />
              {isPremium ? "PREMIUM SHIELD · ELITE" : (user?.subscription_tier || "pro").toUpperCase()}
            </span>
            {!isPremium && (
              <button
                onClick={handleLevelUp}
                disabled={upgrading}
                data-testid="upgrade-btn"
                data-upgrading={upgrading ? "true" : "false"}
                className={`hidden sm:inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-md text-slate-950 transition-transform ${upgrading ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
                style={{
                  background: "linear-gradient(135deg, #a855f7, #06B6D4)",
                  boxShadow: "0 8px 18px -8px rgba(168,85,247,0.6)",
                }}
                title="Upgrade to Premium Shield — prorated instantly"
              >
                {upgrading ? "Upgrading…" : "Level up ↑"}
              </button>
            )}
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-9 h-9 rounded-full border border-white/10"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-sm font-semibold">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
              title="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
        {/* Mobile top-nav removed in favor of the sticky bottom bar below (min 48px thumb targets) */}
      </header>

      <main className="relative z-20 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-28 lg:pb-4">
        <style>{`
          .nav-glow::before {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: inherit;
            pointer-events: none;
            opacity: 0;
            transition: opacity 220ms ease;
            background: radial-gradient(180px circle at var(--gx, 50%) var(--gy, 50%), rgba(16,185,129,0.35), transparent 60%);
            filter: blur(2px);
          }
          .nav-glow:hover::before { opacity: 1; }
          .nav-glow::after {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: inherit;
            pointer-events: none;
            opacity: 0;
            transition: opacity 220ms ease;
            box-shadow: 0 0 0 1px rgba(16,185,129,0.45), 0 0 22px rgba(16,185,129,0.35);
          }
          .nav-glow:hover::after { opacity: 1; }
        `}</style>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, x: 24, filter: "blur(6px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0)" }}
            exit={{ opacity: 0, x: -24, filter: "blur(6px)" }}
            transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
            style={{ willChange: "transform, opacity, filter" }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Desktop-only transparent bottom footer bar — zero geometry drift, no dark background.
          Hidden on mobile where the sticky bottom nav already occupies that viewport edge. */}
      <footer
        data-testid="dashboard-footer-bar"
        className="hidden lg:flex justify-between items-center bg-transparent px-6 py-4 text-xs text-slate-500/60 fallback-blur-none max-w-[1600px] mx-auto w-full"
      >
        <span
          data-testid="footer-copyright"
          className="font-mono uppercase tracking-[0.18em] select-none"
        >
          © 2026 RECEIPT SHEETS
        </span>
        <nav
          className="flex items-center gap-5 font-mono uppercase tracking-[0.18em]"
          aria-label="Footer navigation"
        >
          <a
            href="/privacy"
            data-testid="footer-link-privacy"
            className="hover:text-slate-300 transition-colors duration-150"
          >
            PRIVACY
          </a>
          <span className="text-slate-700/50" aria-hidden="true">·</span>
          <a
            href="/terms"
            data-testid="footer-link-terms"
            className="hover:text-slate-300 transition-colors duration-150"
          >
            TERMS
          </a>
          <span className="text-slate-700/50" aria-hidden="true">·</span>
          <span
            data-testid="footer-status"
            className="inline-flex items-center gap-1.5"
          >
            <span
              aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full bg-emerald-400/70 inline-block"
              style={{ boxShadow: "0 0 6px rgba(52,211,153,0.6)" }}
            />
            STATUS
          </span>
        </nav>
      </footer>

      {/* Mobile bottom nav — sticky, translucent glassmorphism, thumb-accessible 48px targets */}
      <nav
        data-testid="mobile-bottom-nav"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass-strong border-t border-white/10"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="max-w-[1600px] mx-auto flex items-stretch justify-between px-1 py-1 overflow-x-auto scroll-touch">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `flex-1 min-w-[64px] flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-lg text-[10px] font-medium tracking-tight ${
                  isActive ? "text-emerald-300 bg-emerald-500/10" : "text-slate-400"
                }`
              }
              style={{ minHeight: 48, WebkitTapHighlightColor: "transparent" }}
            >
              <n.icon size={18} />
              <span className="leading-none whitespace-nowrap">{n.label}</span>
            </NavLink>
          ))}
          {canSeeManual && (
            <button
              type="button"
              onClick={() => setShowManual(true)}
              data-testid="mobile-nav-manual"
              className="flex-1 min-w-[64px] flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-lg text-[10px] font-medium tracking-tight text-slate-400 hover:text-emerald-300"
              style={{ minHeight: 48, WebkitTapHighlightColor: "transparent" }}
              aria-label="Docs & Manual"
            >
              <BookOpen size={18} />
              <span className="leading-none whitespace-nowrap">Docs</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowSupport(true)}
            data-testid="mobile-nav-support"
            className="flex-1 min-w-[64px] flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-lg text-[10px] font-medium tracking-tight text-slate-400 hover:text-cyan-300"
            style={{ minHeight: 48, WebkitTapHighlightColor: "transparent" }}
            aria-label="Help Desk"
          >
            <LifeBuoy size={18} />
            <span className="leading-none whitespace-nowrap">Help</span>
          </button>
        </div>
      </nav>
      <ManualData open={showManual} onClose={() => setShowManual(false)} />
      <SupportVaultDrawer open={showSupport} onClose={() => setShowSupport(false)} />
      {/* Cursor aura — mounted here at the outermost root so it is NEVER inside
          a transform/will-change ancestor that would re-scope position:fixed.
          This guarantees the ring tracks the real viewport at all times. */}
      <SpaceAgeCursorMount />
    </div>
  );
}
